// Facility tags arrive from research in whatever shape the source used:
// "showers", "Hot showers", "Showers", "changing_rooms", "Changing rooms".
// Left alone they render on the page exactly like that, so a Dutch visitor
// reads "beach_volley" on a Dutch page. This module does two jobs: fold the
// variants onto one canonical key, and give that key a translated label.
//
// The canonical list deliberately stops at the facilities that actually recur.
// Anything past the tail is humanised rather than dropped, because a real but
// unusual facility ("sea swing", "wheelchair lift") is worth showing.

// Research wrote facilities as English prose ("Welcome kit with sunscreen",
// "Wooden pontoon into the sea"), while the fixed amenity tags on the same club
// are translated ("Crema solare inclusa", "Pontile sul mare"). Rendered side by
// side that is the same facility listed twice, once per language, which is what
// an Italian visitor saw on every Sicilian club page. These patterns fold the
// prose onto the same canonical key the tag uses, so the pair collapses into
// one chip in the visitor's language.
//
// Order matters: the first match wins, so the specific patterns come first.
const PHRASES: [RegExp, string][] = [
  [/towel/, "towels"],
  [/sunscreen|sun cream|suncream/, "sunscreen"],
  [/pontoon|jetty|pier|mooring|tender/, "jetty"],
  [/beach (service|attendant)|service to the (sunbed|lounger)|bed service/, "bed_service"],
  [/changing (room|cabin)|cabins?\b/, "changing_rooms"],
  [/locker|safe\b/, "lockers"],
  [/(swimming )?pool|jacuzzi|whirlpool/, "pool"],
  [/spa\b|wellness/, "spa"],
  [/gym|fitness/, "gym"],
  [/massage/, "massage"],
  [/yoga/, "yoga"],
  [/kids|children|family|playground|inflatable water park/, "kids_area"],
  [/lifeguard|supervised swimming|first aid|defibrillator/, "lifeguard"],
  [/wheelchair|accessible|ramp|walkway to the water/, "accessible"],
  [/pet|dog/, "pets_allowed"],
  [/wifi|wi-fi/, "wifi"],
  [/card payment|contactless/, "card_payment"],
  [/valet|parking/, "parking"],
  [/shuttle|ferry|boat (service|transfer|excursion|shuttle)|catamaran/, "boat_shuttle"],
  [/canoe|kayak|pedalo|pedalò|sup\b|paddle|snorkel|diving|windsurf|sailing|jet ski|water sport/, "water_sports"],
  [/boat (rental|hire)|dinghy/, "boat_rental"],
  [/volleyball|beach volley/, "beach_volley"],
  [/beach tennis/, "beach_tennis"],
  [/tennis|football|table tennis|board game|beach games/, "sports"],
  [/dj\b|dj set/, "dj"],
  [/live music|piano bar|music/, "live_music"],
  [/event|wedding|catering|animation|entertainment/, "events"],
  [/pizzeria|gelateria|sushi|bistro|restaurant|dining|takeaway|breakfast/, "restaurant"],
  [/bar\b|cocktail|aperitivo|drinks|wine cellar|granita/, "bar"],
  [/shower|foot wash/, "showers"],
  [/toilet|wc\b|bathroom/, "toilets"],
  [/bali|balinese|cabana|pagoda|gazebo|canopy|canopies|tent/, "cabanas"],
  [/umbrella|parasol|sunshade|shade/, "umbrellas"],
  [/sunbed|lounger|deck ?chair|daybed|big bed|solarium|sun deck|sun terrace/, "sunbeds"],
  [/terrace|veranda|deck\b/, "terrace"],
  [/sea[- ]?view|panoramic|sea-?view/, "sea_view"],
  [/sea access|beach access|direct sea|ladder/, "sea_access"],
  [/lounge|chill|vip/, "lounge"],
  [/boutique|shop|store/, "shop"],
  [/adults only/, "adults_only"],
  [/nightclub|club night/, "nightclub"],
  [/promenade|garden|pine grove|dune/, "grounds"],
];

const SYNONYMS: Record<string, string> = {
  beach_bar: "bar",
  cocktail_bar: "bar",
  lounge_bar: "bar",
  snack_bar: "bar",
  hot_showers: "showers",
  sunbed_service: "sunbeds",
  sunbed_and_umbrella_service: "sunbeds",
  sun_loungers: "sunbeds",
  deck_chairs: "sunbeds",
  sun_beds: "sunbeds",
  parasols: "umbrellas",
  sunshades: "umbrellas",
  dj_live_music: "dj",
  live_dj: "dj",
  music: "live_music",
  wc: "toilets",
  wifi_free: "wifi",
  free_wifi: "wifi",
  car_park: "parking",
  kids_club: "kids_area",
  childrens_playground: "playground",
  pets: "pets_allowed",
  pet_friendly: "pets_allowed",
  dogs_allowed: "pets_allowed",
  wheelchair_accessible: "accessible",
  beach_volleyball: "beach_volley",
  jet_ski: "water_sports",
  water_sport: "water_sports",
  cabana: "cabanas",
  cabins: "changing_rooms",
  changing_cabins: "changing_rooms",
  private_beach: "beach",
  sea_views: "sea_view",
};

// Canonical keys that have a translation in every dictionary, as `fac_<key>`.
export const KNOWN_FACILITIES = [
  "bar",
  "restaurant",
  "showers",
  "sunbeds",
  "umbrellas",
  "dj",
  "live_music",
  "parking",
  "accessible",
  "wifi",
  "pool",
  "terrace",
  "toilets",
  "kids_area",
  "playground",
  "sea_view",
  "water_sports",
  "beach_volley",
  "beach_tennis",
  "changing_rooms",
  "cabanas",
  "lifeguard",
  "pets_allowed",
  "events",
  "jetty",
  "massage",
  "card_payment",
  "boat_rental",
  "towels",
  "sunscreen",
  "bed_service",
  "lockers",
  "spa",
  "gym",
  "yoga",
  "sports",
  "boat_shuttle",
  "sea_access",
  "lounge",
  "shop",
  "adults_only",
  "nightclub",
  "grounds",
] as const;

const KNOWN = new Set<string>(KNOWN_FACILITIES);

// The fixed amenity tags and the researched facility strings describe the same
// world, so they have to land in the same namespace or the page shows both.
const AMENITY_TO_FACILITY: Record<string, string> = {
  bed_service: "bed_service",
  jetty: "jetty",
  lunch: "restaurant",
  towels: "towels",
  sunscreen: "sunscreen",
  cushions: "sunbeds",
  party: "events",
};

/** Fold a raw research value onto its canonical key. */
export function canonicalFacility(raw: string): string {
  const prose = raw.replace(/\([^)]*\)/g, " ").toLowerCase();
  for (const [re, key] of PHRASES) if (re.test(prose)) return key;

  const key = raw
    // Research sometimes qualifies a facility with the venue's own name, as in
    // "Restaurant (Lo Stravizio)". The qualifier is not a facility, and
    // keeping it would split one facet into as many facets as there are clubs.
    .replace(/\([^)]*\)/g, " ")
    .trim()
    .toLowerCase()
    .replace(/[\s/&-]+/g, "_")
    .replace(/[^a-z_]/g, "")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
  return SYNONYMS[key] ?? key;
}

/**
 * A label a person can read, in their own language where we have one.
 * Unknown-but-real values fall back to sentence case rather than vanishing.
 */
export function facilityLabel(raw: string, t: (k: string) => string): string {
  const key = canonicalFacility(raw);
  if (KNOWN.has(key)) return t(`fac_${key}`);
  const words = key.replace(/_/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : raw;
}

/**
 * Canonical, de-duplicated facilities for one club, in a stable order.
 * Pass the fixed amenity tags too: they fold into the same set, which is what
 * stops "Beach towels" and "Teli mare inclusi" appearing as two chips.
 */
export function normalizeFacilities(list: string[], amenities: string[] = []): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  const push = (key: string) => {
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(key);
  };
  for (const raw of list) push(canonicalFacility(raw));
  // Tags second: a researched string carries more detail, so where both exist
  // the facility list decides the key and the tag simply finds it taken.
  for (const a of amenities) push(AMENITY_TO_FACILITY[a] ?? canonicalFacility(a));
  // Known facilities first: they are the ones a visitor is scanning for, and
  // they are the ones that read as a proper word in their language.
  return out.sort((a, b) => Number(KNOWN.has(b)) - Number(KNOWN.has(a)));
}
